<template>
  <div class="message-container d-flex flex-column chat-bubbles gap-md">
    <ChatBubble>
      <slot>
        <p>{{ message.clientMessage }}</p>
      </slot>
    </ChatBubble>
    <ChatBubble
      v-if="showIncomingMessage"
      incoming
      :class="{ 'loading': !message.llmMessage }">
      <slot>
        <FadeTransition group>
          <p v-if="message.llmMessage">
            {{ message.llmMessage }}
          </p>
          <div
            v-else
            class="d-flex gap-sm justify-content-between"
            :class="showEllipsis ? 'align-items-center' : 'align-items-start'">
            <SvgSparkle
              class="text-white sparkle"
              width="26"
              height="26"/>
            <div
              v-if="showEllipsis"
              class="d-flex gap-xxs align-items-center">
              <div
                v-for="index in 3"
                :key="index"
                :class="['dot', { active: activeDotIndex === index }]"/>
            </div>
            <p
              v-else
              class="gradient-text">
              Searching through the Big Closet
            </p>
          </div>
        </FadeTransition>
      </slot>
    </ChatBubble>
    <FadeTransition>
      <div v-if="showReccomendationsSection">
        <BaseCarousel
          class="mb-md"
          :button-container="scrollButtons"
          :indicator-container="indicators"
          :show-indicators="showIndicatorsAndButtons"
          :show-buttons="showIndicatorsAndButtons"
          :items="message?.recommendations"
          :items-per-slide="{ mobile: 2, tablet: 2, desktop: 2 }"
          :interval="3000"
          auto-scroll-behavior="once">
          <template #default="{ item }">
            <StyleColorPreview
              :key="item.id"
              :style-color="item"
              :description-props="{
                hideDetails: true,
              }"
              :style-color-set="styleColorSet"
              size="auto"
              class="mb-lg"
              @click="handleStyleImageClick()"/>
          </template>
        </BaseCarousel>
        <div
          v-if="showIndicatorsAndButtons"
          ref="indicators"
          class="indicators-container"/>
        <div
          v-if="showIndicatorsAndButtons"
          ref="scrollButtons"
          class="buttons-container"
          :style="{ '--width-of-indicators': (widthOfIndicators + 32) + 'px' }"/>
      </div>
    </FadeTransition>

    <div
      v-if="message?.recommendations?.length > 0"
      class="feedback w-100"
      :style="{ 'height': feedbackFormToggled ? '100%' : '40px' }">
      <FadeTransition group>
        <div
          v-if="showFeedbackButtons"
          class="mx-md">
          <PlainButton
            class="p-1 me-sm"
            @click="onRateResponse(message.serverId, 'like')">
            <SvgLike
              width="32"
              height="32"
              :filled="messageFeedbacks[message.serverId]?.rating === 1"/>
          </PlainButton>
          <PlainButton
            class="p-0"
            @click="onRateResponse(message.serverId, 'dislike')">
            <SvgDislike
              width="32"
              height="32"
              :filled="messageFeedbacks[message.serverId]?.rating === -1"/>
          </PlainButton>
        </div>
      </FadeTransition>
      <div
        v-if="feedbackFormToggled"
        class="feedback-form bg-origami text-armor d-flex flex-column w-100 m-0 mb-md px-lgp pb-sm pt-md">
        <div class="d-flex justify-content-between">
          <p class="bold">
            Feedback
          </p>
          <PlainButton
            class="p-0"
            @click="feedbackFormToggled = false">
            <SvgX color="armor"/>
          </PlainButton>
        </div>
        <div class="d-flex gap-sm my-sm">
          <BaseButton
            v-for="option in ratingDetailOptions"
            :key="option.label"
            :text="option.label"
            :variant="messageFeedbacks[message.serverId]?.detail === option.value ? 'primary' : 'secondary'"
            class="px-sm condensed"
            @click="onRatingsDetails(message.serverId, option.value)"/>
        </div>
        <BaseTextArea
          v-if="showTextInput"
          v-model="messageFeedbacks[message.serverId].suggestion"
          placeholder="Have a suggestion? Let us know how we can improve this experience."/>
        <BaseButton
          v-if="showTextInput"
          class="ms-auto mt-sm condensed"
          text="Send"
          @click="onSend(message.serverId)"/>
      </div>
    </div>
    <ChatBubble
      v-if="showFollowUpPrompt"
      incoming
      class="follow-up-prompt">
      <slot>
        <p>
          {{ message.llmFollowUpPrompt }}
        </p>
      </slot>
    </ChatBubble>
    <div
      v-if="message?.feedbackError"
      class="m-md align-self-center border bg-origami mx-md">
      <p class="fine-print text-orchid text-center  my-xs mx-md">
        {{ message.feedbackError }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useStylingChatbotStore } from '@/stores/stylingChatbot.js'
import { useElementSize } from '@/composables/element.js'
import { useDrawers } from '@/composables/drawers.js'
import FadeTransition from '@shared/components/ADORN/transitions/FadeTransition.vue'
import BaseButton from '@shared/components/ADORN/BaseButton'
import BaseTextArea from '@/components/global/BaseTextArea.vue'
import ChatBubble from '@shared/components/ADORN/ChatBubble.vue'
import PlainButton from '../global/sequin/PlainButton.vue'
import StyleColorPreview from '@shared/components/ADORN/StyleColorPreview.vue'
import SvgDislike from '@/components/global/svg/SvgDislike.vue'
import SvgLike from '@/components/global/svg/SvgLikeFillAndEmpty.vue'
import SvgSparkle from '@/components/global/svg/SvgSparkle.vue'
import SvgX from '@/components/global/svg/SvgX.vue'
import BaseCarousel from '@shared/components/ADORN/BaseCarousel.vue'
const props = defineProps({
  message: {
    type: Object,
    required: true
  }
})

const scrollButtons = ref(null)
const indicators = ref(null)
const stylingChatbotStore = useStylingChatbotStore()
const { chatbotDrawer } = useDrawers()
const { postFeedback } = stylingChatbotStore
const size = useElementSize(indicators)
const widthOfIndicators = computed(() => size.value.width)

const {
  ratingDetailOptions,
  messageFeedbacks
} = storeToRefs(stylingChatbotStore)

const showRecommendations = ref(false)
const showFeedbackButtons = ref(false)
const showIncomingMessage = ref(false)
const showEllipsis = ref(false)
const showFollowUpPrompt = ref(false)
const feedbackFormToggled = ref(false)
const activeDotIndex = ref(0)

const styleColorSet = computed(() => {
  return {
    name: props.message?.llmMessage || 'Styling Chatbot',
    source: 'stylingchatbot',
    sourceType: 'stylingchatbot',
  }
})
const showReccomendationsSection = computed(() => {
  return showRecommendations.value && props.message?.recommendations.length > 0
})
const showTextInput = computed(() => {
  return messageFeedbacks.value[props.message.serverId]?.detail === 3
})
const showIndicatorsAndButtons = computed(() => {
  return props.message?.recommendations && props.message?.recommendations.length > 2
})

async function onRateResponse(id, rating) {
  if (rating === 'like') {
    feedbackFormToggled.value = false
    messageFeedbacks.value[id] = { ...messageFeedbacks.value[id], rating: 1, suggestion: '', detail: null }
  } else if (rating === 'dislike') {
    feedbackFormToggled.value = true
    messageFeedbacks.value[id] = { ...messageFeedbacks.value[id], rating: -1 }
  }
  postFeedback(messageFeedbacks.value[id])
}
async function onRatingsDetails(id, ratingDetail) {
  messageFeedbacks.value[id] = { ...messageFeedbacks.value[id], detail: ratingDetail }
  postFeedback(messageFeedbacks.value[id])
}
function onSend(id) {
  feedbackFormToggled.value = false
  postFeedback(messageFeedbacks.value[id])
}
function handleStyleImageClick() {
  chatbotDrawer.minimize()
}
// watch llmMessage, set timeouts to show elements
watch(() => props.message?.clientMessage, () => {
  setTimeout(() => {
    showIncomingMessage.value = true
  }, 2000)
  setTimeout(() => {
    showEllipsis.value = true
  }, 12000)
}, { immediate: true })

watch(() => props.message?.llmMessage, (newValue) => {
  if (newValue) {
    setTimeout(() => { showRecommendations.value = true }, 4000)
    setTimeout(() => { showFeedbackButtons.value = true }, 5000)
    setTimeout(() => {
      showFollowUpPrompt.value = props.message?.llmFollowUpPrompt ? true : false
    }, (props.message?.recommendations ? 7000 : 4000))
  }
}, { immediate: true })

onMounted(() => {
  showRecommendations.value = !!props.message?.recommendations
  showFeedbackButtons.value = !!props.message?.recommendations
  showIncomingMessage.value = !!props.message?.llmMessage
  showFollowUpPrompt.value = !!props.message?.llmFollowUpPrompt
  setInterval(() => {
    activeDotIndex.value = activeDotIndex.value % 3 + 1
  }, 700)
})
</script>

<style lang="scss" scoped>
:deep(.indicators-container) {
  margin: auto;
  width: fit-content;
  height: 32px;
  display: flex;
  align-items: center;
}

:deep(.absolute-buttons) {
  margin: 0 auto;
  width: fit-content;
  display: flex;

  gap: var(--width-of-indicators, 0);
}

.buttons-container {
  position: relative;
  bottom: 32px;
  margin-bottom: -32px;
}

.feedback-button-spacing {
  height: 40px;
}

.dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: $white;
  opacity: 0.5;
  transition: all 0.3s ease-in-out;
}

.dot.active {
  width: 8px;
  height: 8px;
  opacity: 1;
}

.follow-up-prompt {
  --bubble-animation-duration: 1s;
}

.gradient-text {
  display: inline-block;
  background-image: linear-gradient(
    120deg,
    rgba(255, 255, 255, 0.4) 0%,
    rgba(255, 255, 255, 0.8) 25%,
    rgba(255, 255, 255, 0.4) 50%,
    rgba(255, 255, 255, 0.8) 75%,
    rgba(255, 255, 255, 0.4) 100%
  );
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  animation: shimmer 3s linear infinite;
}

@keyframes shimmer {
  0% {
    background-position: -100% 0%;
  }
  100% {
    background-position: 100% 0%;
  }
}

.sparkle {
  min-width: 26px;
  min-height: 26px;
}

:deep(.chat-bubble.incoming.loading) {
  width: calc(100% - ($spacing-md + $spacing-xxl));
}
</style>
